import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Box, Typography } from '@mui/material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Hub } from 'aws-amplify';
import LoginContainer from '../Layout/LoginContainer';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { clearState, setShowTokenExpiredModal } from '../../app/store/userSlice';
import { signInWithEntra } from '../../services/auth';

const MicrosoftLogo = () => (
  <Box component="svg" viewBox="0 0 21 21" sx={{ width: 18, height: 18, mr: 1.5 }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </Box>
);

// Present when the page has just been reloaded by the OAuth redirect back from Cognito/Entra.
const hasOAuthCallbackParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

// TPN Admins granted access through Microsoft Entra sign in here. SSO-only: no email/password fallback.
export default function SsoLoginPage() {
  const [ssoError, setSsoError] = useState<string | null>(null);
  // Hide the sign-in button while an OAuth callback is still being processed, to avoid it
  // flashing on screen before the redirect-on-success effect below has a chance to fire.
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(hasOAuthCallbackParams);
  const { user } = useAppSelector(state => state.user);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { route } = useAuthenticator((context) => [context.route]);
  const nav = useNavigate();

  useEffect(() => {
    dispatch(setShowTokenExpiredModal(false));

    // Populating `user` on a successful sign-in is handled app-wide in App.tsx, since this page
    // isn't necessarily mounted when the OAuth redirect lands (e.g. if it lands on a protected
    // route directly). This listener only needs to react to a failed callback.
    const listener = (data: any) => {
      const event = data.payload.event;
      if (event === 'signIn_failure' || event === 'cognitoHostedUI_failure') {
        setIsProcessingRedirect(false);
        setSsoError('Unable to complete Microsoft sign-in. Please try again or contact support.');
      }
    };

    Hub.listen('auth', listener);

    return () => {
      Hub.remove('auth', listener);
    };
  }, [dispatch]);

  useEffect(() => {
    if (route !== 'authenticated' || !user) return;
    dispatch(clearState());

    const redirect = searchParams.get('redirect');
    const navTo = redirect || '/registry';
    nav(navTo, { replace: true });
  }, [route, user, nav, searchParams]);

  const handleMicrosoftSignIn = async () => {
    try {
      setSsoError(null);
      await signInWithEntra();
    } catch (err) {
      console.error('Microsoft sign-in failed:', err);
      setSsoError('Unable to start Microsoft sign-in. Please try again or contact support.');
    }
  };

  // Already authenticated, or an OAuth callback is still resolving - avoid flashing the sign-in
  // button before the redirect effect above fires (or before we know sign-in failed).
  if (isProcessingRedirect || (route === 'authenticated' && user)) {
    return null;
  }

  return (
    <LoginContainer>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 300, mb: 1 }}>
          TPN Admin Sign In
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          This page is only for TPN Admins who have been granted access through Microsoft Entra.<br/>
          If you don&apos;t have Entra access, please use the standard sign-in page instead.
        </Typography>
        <Button
          variant="outlined"
          size="large"
          onClick={handleMicrosoftSignIn}
          sx={{ textTransform: 'none' }}
        >
          <MicrosoftLogo />
          Sign in with Microsoft
        </Button>
        {ssoError && <Alert severity="error" sx={{ mt: 2 }}>{ssoError}</Alert>}
      </Box>
    </LoginContainer>
  );
}
